import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
  Person as UserIcon,
  PersonOutline as GuestIcon
} from '@mui/icons-material';
import { Checkbox } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../contexts/ApiContext';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  equalFitCols,
  fitTruncateSx,
  fitChipSx,
} from '../utils/fitTable';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'user' | 'guest';
  is_active: boolean;
  last_login: string | null;
  created_at: string;
  updated_at: string;
}

export const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: 'user' as 'admin' | 'user' | 'guest',
    is_active: true
  });
  const [addForm, setAddForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'user' as 'admin' | 'user' | 'guest'
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/users');
      setUsers(response.data.users);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      role: user.role,
      is_active: user.is_active
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;

    try {
      await api.put(`/api/admin/users/${selectedUser.id}`, editForm);
      setSuccess('User updated successfully');
      setEditDialogOpen(false);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await api.delete(`/api/admin/users/${selectedUser.id}`);
      setSuccess('User deleted successfully');
      setDeleteDialogOpen(false);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleAddUser = async () => {
    try {
      await api.post('/api/admin/users', addForm);
      setSuccess('User created successfully');
      setAddDialogOpen(false);
      setAddForm({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        role: 'user'
      });
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create user');
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // Select all users except the current user
      setSelectedUsers(users.filter(user => user.id !== currentUser?.id).map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectOne = (userId: number) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleBulkDelete = async () => {
    try {
      setLoading(true);
      const results = {
        successful: [] as number[],
        failed: [] as { id: number; error: string; status?: number }[]
      };

      // Delete users one by one and track results
      for (const userId of selectedUsers) {
        try {
          await api.delete(`/api/admin/users/${userId}`);
          results.successful.push(userId);
        } catch (err: any) {
          let errorMessage = 'Unknown error';
          const status = err.response?.status;
          
          if (status === 400) {
            errorMessage = err.response?.data?.error || 'Bad Request - Cannot delete this user';
          } else if (status === 404) {
            errorMessage = 'User not found';
          } else if (status === 500) {
            errorMessage = 'Server error - User may have related data that prevents deletion';
          } else if (err.response?.data?.error) {
            errorMessage = err.response.data.error;
          } else if (err.response?.statusText) {
            errorMessage = err.response.statusText;
          }
          
          results.failed.push({ id: userId, error: errorMessage, status });
          console.error(`Failed to delete user ${userId} (${status}):`, err);
        }
      }

      // Provide detailed feedback
      if (results.successful.length > 0 && results.failed.length === 0) {
        setSuccess(`Successfully deleted ${results.successful.length} user(s)`);
      } else if (results.successful.length > 0 && results.failed.length > 0) {
        setSuccess(`Successfully deleted ${results.successful.length} user(s). ${results.failed.length} failed.`);
        
        // Group errors by type for better user understanding
        const selfDeleteErrors = results.failed.filter(f => f.error.includes('own account'));
        const serverErrors = results.failed.filter(f => f.error.includes('Server error'));
        const notFoundErrors = results.failed.filter(f => f.error.includes('not found'));
        const otherErrors = results.failed.filter(f => 
          !f.error.includes('own account') && 
          !f.error.includes('Server error') && 
          !f.error.includes('not found')
        );
        
        let errorMessage = 'Some deletions failed: ';
        const errorDetails = [];
        
        if (selfDeleteErrors.length > 0) {
          errorDetails.push(`Cannot delete your own account (${selfDeleteErrors.length})`);
        }
        if (serverErrors.length > 0) {
          errorDetails.push(`Users with related data (${serverErrors.length})`);
        }
        if (notFoundErrors.length > 0) {
          errorDetails.push(`Users not found (${notFoundErrors.length})`);
        }
        if (otherErrors.length > 0) {
          errorDetails.push(`Other errors (${otherErrors.length})`);
        }
        
        setError(errorMessage + errorDetails.join(', ') + '. Check console for details.');
      } else {
        setError('Failed to delete all selected users. Please check console for details.');
      }

      setBulkDeleteDialogOpen(false);
      setSelectedUsers([]);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete users');
      console.error('Bulk delete error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <AdminIcon fontSize="small" />;
      case 'user': return <UserIcon fontSize="small" />;
      case 'guest': return <GuestIcon fontSize="small" />;
      default: return <UserIcon fontSize="small" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'user': return 'primary';
      case 'guest': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">User Management</Typography>
        <Box>
          {selectedUsers.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setBulkDeleteDialogOpen(true)}
              sx={{ mr: 1 }}
            >
              Delete Selected ({selectedUsers.length})
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
        <Table size="small" sx={FIT_TABLE_SX}>
          <FitColgroup
            cols={equalFitCols(
              ['select', 'name', 'email', 'role', 'status', 'lastLogin', 'actions'],
              { flexKey: 'email' }
            )}
          />
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedUsers.length === users.filter(user => user.id !== currentUser?.id).length && users.filter(user => user.id !== currentUser?.id).length > 0}
                  indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.filter(user => user.id !== currentUser?.id).length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => handleSelectOne(user.id)}
                    disabled={user.id === currentUser?.id}
                  />
                </TableCell>
                <TableCell>
                  <Box component="span" sx={fitTruncateSx} title={`${user.first_name} ${user.last_name}`}>
                    {user.first_name} {user.last_name}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box component="span" sx={fitTruncateSx} title={user.email}>
                    {user.email}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getRoleIcon(user.role)}
                    label={user.role.toUpperCase()}
                    color={getRoleColor(user.role)}
                    size="small"
                    sx={fitChipSx}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.is_active ? 'Active' : 'Inactive'}
                    color={user.is_active ? 'success' : 'default'}
                    size="small"
                    sx={fitChipSx}
                  />
                </TableCell>
                <TableCell>
                  <Box component="span" sx={fitTruncateSx}>
                    {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                  </Box>
                </TableCell>
                <TableCell align="right" className="fit-table-no-clip">
                  <Tooltip title="Edit User">
                    <IconButton onClick={() => handleEditUser(user)} size="small">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete User">
                    <IconButton
                      onClick={() => {
                        setSelectedUser(user);
                        setDeleteDialogOpen(true);
                      }}
                      size="small"
                      disabled={user.id === currentUser?.id}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="First Name"
            value={addForm.first_name}
            onChange={(e) => setAddForm({ ...addForm, first_name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Last Name"
            value={addForm.last_name}
            onChange={(e) => setAddForm({ ...addForm, last_name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={addForm.email}
            onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={addForm.password}
            onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={addForm.role}
              label="Role"
              onChange={(e) => setAddForm({ ...addForm, role: e.target.value as 'admin' | 'user' | 'guest' })}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="guest">Guest</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddUser} variant="contained">Add User</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="First Name"
            value={editForm.first_name}
            onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Last Name"
            value={editForm.last_name}
            onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={editForm.email}
            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={editForm.role}
              label="Role"
              onChange={(e) => setEditForm({ ...editForm, role: e.target.value as 'admin' | 'user' | 'guest' })}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="guest">Guest</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          Are you sure you want to delete {selectedUser?.first_name} {selectedUser?.last_name}?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)}>
        <DialogTitle>Delete Selected Users</DialogTitle>
        <DialogContent>
          Are you sure you want to delete {selectedUsers.length} selected user(s)? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleBulkDelete} 
            color="error" 
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : undefined}
          >
            {loading ? 'Deleting...' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;

